/**
 * Global Glossary Store
 * Manages SynonymGroups independently from projects.
 * This is the single source of truth for terminology data.
 */
import { create } from 'zustand';
import { SynonymGroup } from '../types';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';

interface GlossaryState {
    // Data
    synonymGroups: SynonymGroup[];
    isLoaded: boolean;

    // Actions
    loadGlossary: () => Promise<void>;
    saveGlossary: () => Promise<void>;

    // CRUD
    addGroup: (group: SynonymGroup) => void;
    updateGroup: (group: SynonymGroup) => void;
    deleteGroup: (groupId: string) => void;
    setGroups: (groups: SynonymGroup[]) => void;
}

export const useGlossaryStore = create<GlossaryState>((set, get) => ({
    synonymGroups: [],
    isLoaded: false,

    loadGlossary: async () => {
        const data = await fileSystemPersistence.loadGlossary();
        if (data?.synonymGroups && data.synonymGroups.length > 0) {
            // glossary.json exists and has data
            set({ synonymGroups: data.synonymGroups, isLoaded: true });
        } else {
            // No global glossary yet - try to migrate from projects
            try {
                const projects = await fileSystemPersistence.loadProjects();
                const allGroups: SynonymGroup[] = [];
                const seenIds = new Set<string>();

                // Collect synonymGroups from all projects
                for (const project of projects) {
                    if (project.synonymGroups && Array.isArray(project.synonymGroups)) {
                        for (const group of project.synonymGroups) {
                            if (!seenIds.has(group.id)) {
                                seenIds.add(group.id);
                                allGroups.push(group);
                            }
                        }
                    }
                }

                if (allGroups.length > 0) {
                    console.log(`[Glossary] Migrated ${allGroups.length} groups from projects`);
                    // Save to global glossary
                    await fileSystemPersistence.saveGlossary({ synonymGroups: allGroups });
                    set({ synonymGroups: allGroups, isLoaded: true });
                } else {
                    set({ isLoaded: true });
                }
            } catch (err) {
                console.error('[Glossary] Migration failed:', err);
                set({ isLoaded: true });
            }
        }
    },

    saveGlossary: async () => {
        const { synonymGroups } = get();
        await fileSystemPersistence.saveGlossary({ synonymGroups });
    },

    addGroup: (group) => {
        set(state => {
            const newGroups = [group, ...state.synonymGroups];
            // Auto-save
            fileSystemPersistence.saveGlossary({ synonymGroups: newGroups });
            return { synonymGroups: newGroups };
        });
    },

    updateGroup: (group) => {
        set(state => {
            const newGroups = state.synonymGroups.map(g =>
                g.id === group.id ? group : g
            );
            fileSystemPersistence.saveGlossary({ synonymGroups: newGroups });
            return { synonymGroups: newGroups };
        });
    },

    deleteGroup: (groupId) => {
        set(state => {
            const newGroups = state.synonymGroups.filter(g => g.id !== groupId);
            fileSystemPersistence.saveGlossary({ synonymGroups: newGroups });
            return { synonymGroups: newGroups };
        });
    },

    setGroups: (groups) => {
        set({ synonymGroups: groups });
        fileSystemPersistence.saveGlossary({ synonymGroups: groups });
    }
}));
